import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RiDeleteBinLine } from 'react-icons/ri'
import useFetch, { CachePolicies } from 'use-http'
import useToast from '../../../hooks/useToast'
import {
  DeletegeneratedImagesResponse,
  GenerateImageErrorResponse,
} from '../../../interfaces/GenerateImage'
import { UserContext } from '../../../providers/UserProvider'

interface DeleteGeneratedImageModalProps {
  isOpen: boolean
  onClose(): void
  imageIds: string[]
  refetch?(): void
}

const DeleteGeneratedImageModal = ({
  isOpen,
  onClose,
  imageIds,
  refetch,
}: DeleteGeneratedImageModalProps): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const [isSubmitting, setSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const { user } = useContext(UserContext)

  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const deleteGeneratedImages = async (): Promise<void> => {
    setError(null)

    const res = (await request.delete(
      `/api/media-library/${user?.clientNumber}/generate-image`,
      {
        imageIds,
      },
    )) as DeletegeneratedImagesResponse | GenerateImageErrorResponse

    if (request.error) {
      setError(
        (res as GenerateImageErrorResponse).message ??
          t(
            'error_in_deleting_images',
            'Error in deleting the selected generated image(s), please try again later.',
          ),
      )
    } else {
      toast({
        description: t(
          'image_deleted_successfully',
          'Selected generated image(s) have been deleted successfully.',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
      })
      refetch?.()
      onClose()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
        setError(null)
      }}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Icon
            as={RiDeleteBinLine}
            color="appBattleshipGrey"
            backgroundColor="appPaleGrey"
            boxSize={6}
            padding="0.1em"
            borderRadius="full"
            marginRight="0.5em"
          />
          {t('delete_generated_image', 'Delete generated image')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {error && (
            <Alert status="error" mb={4} minW="unset">
              <AlertIcon />
              <Box flex="1">
                <AlertDescription>{error}</AlertDescription>
              </Box>
            </Alert>
          )}
          <Text color="appBattleshipGrey">
            {t(
              'delete_generated_image_description',
              'Do you want to delete the selected generated image? Note that the URL will keep working.',
            )}
          </Text>
        </ModalBody>
        <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
          <Button onClick={onClose} variant="outline">
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            colorScheme="appRed"
            isDisabled={isSubmitting}
            onClick={async () => {
              setSubmitting(true)
              await deleteGeneratedImages()
              setSubmitting(false)
            }}
          >
            {t('submit', 'Submit')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default DeleteGeneratedImageModal
